import React, { SyntheticEvent, ChangeEvent, RefObject, createRef } from 'react';
import Modal from '@components/Modal/Modal';
import { compose } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { getAdminMinioCredentials_getAdminMinioCredentials } from '@api/graphql/types';
import { ApplicationState } from 'reducers/';
import { connect, DispatchProp } from 'react-redux';
import { getAdminMinio } from '@root/utils/GraphQL';
import { storeAdminMinioCredentials } from '@actions/fileActions';
import Notification from './Notification';
import { Client, BucketItem } from 'minio';
import { ToastActionType } from '@actions/toastActions';
import { ToastLevelType } from './Toast/Toast';

type PropType = {
    value: string;
    visible: boolean;
    prefix: string;
    onClose: () => void;
    type: 'image' | 'file';
    onSubmit: (value: string) => void;
} & InjectedIntlProps;

interface ReduxProps {
    credentials: getAdminMinioCredentials_getAdminMinioCredentials | null;
}

type Props = ReduxProps & PropType & DispatchProp & InjectedIntlProps;

interface State {
    search: string;
    value: string;
    files: BucketItem[];
    endpoint: string | null;
    bucket: string | null;
}

class FileModal extends React.Component<Props, State> {
    private client: Client;

    public state = {
        search: '',
        fileError: '',
        value: this.props.value,
        files: [],
        endpoint: '',
        bucket: '',
    };

    private fileInputRef: RefObject<HTMLInputElement> = createRef();

    public componentDidMount() {
        this.loadFiles();
    }

    private handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length) {
            const file = e.target.files[0];
            if (this.fileExists(file.name)) {
                this.props.dispatch({type: ToastActionType.addToast, toast: {
                    id: `exists-${file.name}`,
                    text: this.props.intl.formatMessage({id: 'component.FileSelector.fileExists'}),
                    type: ToastLevelType.error, timeout: 2000,
                }});
                this.fileInputRef.current!.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onloadend = this.handleFile(file, reader);
            reader.readAsDataURL(file);
            this.fileInputRef.current!.value = '';
        }
    }

    private handleFile = (file: File, reader: any) => async (e: any) => {
        try {
            const fileName = `${this.props.prefix ? `${this.props.prefix}/` : ''}${file.name.replace(/\s/g, '-')}`;
            const buf = new Buffer(reader.result.replace(/^data:\w+\/\w+;base64,/, ""), 'base64');

            const credentials = await this.getAdminMinioCredentials();
            if (credentials && credentials.minio_access_key && credentials.minio_secret_key && credentials.minio_endpoint && credentials.minio_bucket) {
                const minioClient = new Client({
                    endPoint: new URL(credentials.minio_endpoint).host,
                    accessKey: credentials.minio_access_key,
                    secretKey: credentials.minio_secret_key,
                });

                minioClient.putObject('media', fileName, buf, file.size, { 'Content-Type': file.type, 'Content-Encoding': 'base64' }, (err, etag) => {
                    if (!err) {
                        this.loadFiles();
                        this.props.dispatch({type: ToastActionType.addToast, toast: {
                            id: `upload-${file.name}`,
                            text: this.props.intl.formatMessage({id: 'component.FileSelector.successUpload'}),
                            type: ToastLevelType.success, timeout: 2000,
                        }});

                        this.setState({value: `${credentials.minio_endpoint}/${credentials.minio_bucket}/${fileName}`});
                    }
                });
            }
        } catch {
            Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
        }
    }


    private getAdminMinioCredentials = async (): Promise<getAdminMinioCredentials_getAdminMinioCredentials | null> => {
        const { credentials } = this.props;
        const areCredentialsStored = credentials && credentials.minio_access_key && credentials.minio_secret_key && credentials.minio_endpoint && credentials.minio_bucket;
        if (areCredentialsStored) {
            return credentials;
        } else {
            try {
                const queriedCredentials = await getAdminMinio();
                this.props.dispatch(storeAdminMinioCredentials(queriedCredentials));
                if (areCredentialsStored) {
                    return credentials;
                } else {
                    throw Error('No credentials!');
                }
            } catch (error) {
                Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
                return null;
            }
        }
    }

    private loadFiles = async () => {
        const credentials = await this.getAdminMinioCredentials();
        if (credentials && credentials.minio_access_key && credentials.minio_secret_key && credentials.minio_endpoint && credentials.minio_bucket) {
            this.client = new Client({
                endPoint: new URL(credentials.minio_endpoint).host,
                accessKey: credentials.minio_access_key,
                secretKey: credentials.minio_secret_key,
            });

            const objectsStream = this.client.listObjects(credentials.minio_bucket, this.props.prefix, true);
            const files: BucketItem[] = [];

            objectsStream.on('data', (obj: BucketItem) => {
                files.push(obj);
                this.setState({files, endpoint: credentials.minio_endpoint, bucket: credentials.minio_bucket});
            });

            objectsStream.on('error', (e) => {
                console.log(e);
            });
        }
    }

    private renderModalButtons = () => {
        return (
                <div className="row">
                    <div className="col-sm-8">
                        <input type="text" className="form-control" value={this.state.value} onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            this.setState({value: e.currentTarget.value});
                        }} />
                    </div>
                    <div className="col-sm-2">
                        <input type="file" hidden={true} style={{ display: 'none' }} ref={this.fileInputRef} onChange={this.handleChange} />
                        <button className="btn btn-primary btn-block" onClick={(e: SyntheticEvent) => {
                            e.preventDefault();
                            this.fileInputRef.current!.click();
                        }}>{this.props.intl.formatMessage({ id: 'common.upload' })}</button>
                    </div>
                    <div className="col-sm-2">
                        <button className="btn btn-warning btn-block pull-right" onClick={(e: SyntheticEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            this.props.onSubmit(this.state.value);
                            this.props.onClose();
                        }}>
                            {this.props.intl.formatMessage({id: 'common.select'})}
                        </button>
                    </div>
                </div>
        );
    }

    private selectFile = (value: string) => (e: SyntheticEvent) => {
        this.setState({value});
    }

    private fileList() {
        return this.state.files.filter((item: BucketItem) => {
            return !this.state.search || item.name.includes(this.state.search);
        }).map(this.renderItem);

    }

    private renderImages = () => {
        return (
            <div className="scrollable file-modal-list">
                <div className="row">
                    {this.fileList()}
                </div>
            </div>
        );
    }

    private renderFiles = () => {
        return (
            <ul className="scrollable file-modal-list">
                {this.fileList()}
            </ul>
        );
    }

    private fileExists(name: string): boolean {
        const file = this.state.files.find((item: BucketItem) => item.name === `${this.props.prefix}/${name}`);
        return file !== undefined;
    }

    private deleteFile = (name: string) => (e: SyntheticEvent) => {
        e.preventDefault();
        if (this.client) {
            this.client.removeObject(this.state.bucket, name, () => {
                this.loadFiles();
                this.props.dispatch({type: ToastActionType.addToast, toast: {
                    id: `delete-${name}`,
                    text: this.props.intl.formatMessage({id: 'component.FileSelector.successDelete'}),
                    type: ToastLevelType.success, timeout: 4000,
                }});
            });
        }
    }

    private renderItem = (file: BucketItem) => {
        const url = `${this.state.endpoint}/${this.state.bucket}/${file.name}`;
        const selected = url === this.state.value;
        const imageDivStyle = {height: '150px', margin: '2px', padding: '2px', border: `${selected ? '5' : '1'}px solid grey`, lineHeight: '130px'};
        const imageStyle = {maxWidth: '100%', maxHeight: '100%', margin: '0 auto', display: 'inline-block', verticalAlign: 'middle'};
        const fileName = file.name.replace(`${this.props.prefix}/`, '');
        return this.props.type === 'image' ?
            (
                <div className="col-sm-4" key={file.name} style={{height: '200px'}}>
                    <button
                        className="btn btn-danger btn-xs"
                        style={{position: "absolute", top: 0, right: '15px'}}
                        onClick={this.deleteFile(file.name)}
                    ><i className="fa fa-trash" /></button>
                    <a onClick={this.selectFile(url)} className={selected ? 'selected' : ''} style={{textAlign: 'center'}}>
                        <div style={imageDivStyle}>
                            <img src={url} style={imageStyle}/><br />
                        </div>
                    </a>
                    <div style={{wordBreak: 'break-all'}}>
                        {fileName}
                    </div>
                </div>
            ) : (
                <li key={file.name}>
                    { selected ?
                        <strong><a onClick={this.selectFile(url)} className={selected ? 'selected' : ''}>{fileName}</a></strong> :
                        <a onClick={this.selectFile(url)} className={selected ? 'selected' : ''}>{fileName}</a>
                    }
                    &nbsp;({file.size} bytes)&nbsp;
                    <button
                        className="btn btn-danger btn-xs"
                        onClick={this.deleteFile(file.name)}
                    >
                        <i className="fa fa-trash" />
                    </button>

                </li>
            );
    }

    private renderBody = () => {
        return this.props.type === 'image' ? this.renderImages() : this.renderFiles();
    }

    private renderHeader = () => {
        const title = this.props.intl.formatMessage({id: 'component.FileSelector.title'}, {prefix: this.props.prefix});
        return (
            <>
                {title && <h4 className="modal-title">{title}</h4>}
                <input type="text" className="form-control" value={this.state.search} onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    this.setState({search: e.currentTarget.value});
                }} />
            </>
        );
    }

    public render() {
        return (
            <Modal
                className="modal-lg"
                visible={this.props.visible}
                renderHeader={this.renderHeader}
                onClose={this.props.onClose}
                renderButtons={this.renderModalButtons}
                renderBody={this.renderBody}
            />
        );
    }
}
const mapStateToProps = ({ file }: ApplicationState): { credentials: getAdminMinioCredentials_getAdminMinioCredentials | null } => {
    return { credentials: file.credentials };
};

export default compose(
    injectIntl,
    connect(mapStateToProps),
)(FileModal);



// WEBPACK FOOTER //
// ./src/components/FileModal.tsx